import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

import Field from '../../form/Field';
import Icon from '../../common/Icon';
import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import Product from '../../../models/Product';
import FormField from '../../../models/FormField';
import RequestState from '../../../types/RequestState';
import CategoryStore from '../../../store/CategoryStore';

const labels = defineMessages({
  postalCode: {
    id: 'addToCartPostalCodeValidation.checkPostalCodeInfo',
    defaultMessage:
      'Search the availabilities of the services with your postal code'
  },
  postalCodePlaceholder: {
    id: 'addToCartPostalCodeValidation.postalCodePlaceholder',
    defaultMessage: 'Enter your postal code'
  }
});

@observer
export class AddToCartPostalCodeValidation extends Component {
  getPostalCodeValidationState = () => {
    const { product, postalCodeField } = this.props;

    if (!postalCodeField || !postalCodeField.value) {
      return { loading: false, ifValidatedService: false };
    }

    const serviceIsLoading =
      product.validatedServicesState.get(
        `${product.id}--${postalCodeField.value}`
      ) === RequestState.LOADING;

    const ifValidatedService = product.ifValidatedServicesExists({
      productId: product.id,
      postalCode: postalCodeField.value
    });

    const validServices = product.ifValidServices({
      productId: product.id,
      postalCode: postalCodeField.value
    });

    return { serviceIsLoading, ifValidatedService, validServices };
  };

  getValidatedServices = (event) => {
    const { product, postalCodeField, handleChange } = this.props;

    const postalCode = event.target.value;
    postalCodeField.setValue(postalCode);
    const validatedServices = product.getValidatedServices({
      productId: product.id,
      postalCode
    });

    if (validatedServices && validatedServices.length > 0) {
      validatedServices.forEach((service) => {
        const isRequired = service.is_required;
        const postalCodeIsRequired = service.requires_area_validation;
        const selectedByDefault = service.selected_by_default;
        const ifValidatedService =
          postalCodeIsRequired &&
          product.getValidatedService({
            productId: product.id,
            postalCode: postalCodeField.value,
            serviceId: service.id
          });

        if (postalCodeIsRequired && (isRequired || selectedByDefault)) {
          const serviceObject = this.createService(
            service.id,
            service.product_id,
            ifValidatedService,
            '',
            {},
            service.text_max_length,
            service.text_required
          );

          handleChange(serviceObject);
        } else {
          if (postalCodeIsRequired) {
            const serviceObject = this.createService(
              service.id,
              service.product_id,
              false,
              '',
              {},
              service.text_max_length,
              service.text_required
            );

            handleChange(serviceObject);
          }
        }
      });
    } else {
      product.additionalServices.forEach((service) => {
        const postalCodeIsRequired = service.requires_area_validation;

        if (postalCodeIsRequired) {
          const serviceObject = this.createService(
            service.id,
            service.product_id,
            service.valid_postal_code,
            '',
            {},
            service.text_max_length,
            service.text_required
          );

          handleChange(serviceObject);
        }
      });
    }
  };

  createService = (
    id,
    productId,
    isSelected,
    text,
    options,
    textMaxLength,
    textRequired
  ) => {
    return {
      id,
      productId,
      isSelected,
      text,
      options,
      textMaxLength,
      textRequired
    };
  };

  renderValidateButton = () => {
    const { ifValidForm, postalCodeField } = this.props;

    const buttonStates = {
      initial: 'primary',
      valid: 'success',
      invalid: 'danger'
    };

    let buttonState = 'initial';

    const {
      serviceIsLoading,
      ifValidatedService,
      validServices
    } = this.getPostalCodeValidationState();

    const buttonDisabled =
      serviceIsLoading ||
      ifValidForm === false ||
      ifValidatedService ||
      !postalCodeField.value;

    if (validServices === false) {
      buttonState = 'invalid';
    }

    if (validServices) {
      buttonState = 'valid';
    }

    return (
      <div
        className="AddToCartPostalCodeValidation__additional-services-postal-code-form-validate-button"
      >
        <Button
          color={buttonStates[buttonState]}
          disabled={buttonDisabled}
        >
          {this.getCheckButtonContent()}
        </Button>
      </div>
    );
  };

  getCheckButtonContent = () => {
    const {
      serviceIsLoading,
      validServices
    } = this.getPostalCodeValidationState();

    if (serviceIsLoading) {
      return <Icon name="circle-o-notch" spin={serviceIsLoading} />;
    }

    if (validServices === false) {
      return (
        <Fragment>
          <Icon name="times" />
          <FormattedMessage {...globalTranslations.notFoundTitle} />
        </Fragment>
      );
    }

    if (validServices) {
      return (
        <Fragment>
          <Icon name="check" />
          <FormattedMessage {...globalTranslations.checkedTitle} />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Icon name="search" />
        <FormattedMessage {...globalTranslations.checkTitle} />
      </Fragment>
    );
  };

  render() {
    const { product, intl, postalCodeField, handleSubmit } = this.props;

    if (!product.ifServiceRequiresPostalCodeValidation()) {
      return null;
    }

    return (
      <Form
        className="AddToCartPostalCodeValidation"
        noValidate
        onSubmit={handleSubmit}
      >
        <FormGroup className="AddToCartPostalCodeValidation__additional-services-postal-code-form">
          <div className="AddToCartPostalCodeValidation__additional-services-postal-code-form-label">
            <Label for="AddToCartForm__postalCode">
              {intl.formatMessage(labels.postalCode)}
            </Label>
          </div>
          <div className="AddToCartPostalCodeValidation__additional-services-postal-code-form-input-row">
            <div className="AddToCartPostalCodeValidation__additional-services-postal-code-form-input">
              <Field
                handleChange={this.getValidatedServices}
                component={Input}
                field={postalCodeField}
                type="text"
                name="postalCode"
                id="AddToCartForm__postalCode"
                placeholder={intl.formatMessage(labels.postalCodePlaceholder)}
              />
            </div>
            {this.renderValidateButton()}
          </div>
        </FormGroup>
      </Form>
    );
  }
}

AddToCartPostalCodeValidation.propTypes = {
  categoryStore: modelOf(CategoryStore).isRequired,
  product: modelOf(Product).isRequired,
  postalCodeField: modelOf(FormField).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  ifValidForm: PropTypes.bool
};

export default inject('categoryStore')(
  injectIntl(AddToCartPostalCodeValidation)
);



// WEBPACK FOOTER //
// ./src/components/cart/AddToCartPostalCodeValidation/index.jsx